import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { ToastContainer } from "react-toastify";
import { Spinner } from "../../components";
import Estimate from "../../model/estimate";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../service/ApiService";
import Status from "../../model/status";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import DatePicker from "react-datepicker";
import AlertService from "../../service/AlertService";
import ToastMessages from "../../model/enum/toast-messages.enum";
import useStyles from "./styles";
import Misc from "../../utils/Misc";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "25rem !important",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  estimate: Estimate;
}

const UpdateStatusModal = ({ estimate }: Props) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const classes = useStyles();
  const [acceptedBy, setAcceptedBy] = useState("");
  const [acceptedAt, setAcceptedAt] = useState<Date | null>(null);
  const [statusId, setStatusId] = useState(estimate.statusId);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [statusChanged, setStatusChanged] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch statuses from the server or use a predefined list
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<Status[]>(
        `${DatabaseEntities.STATUS}?type=Estimates`
      ).then((data) => {
        if (Misc.isArrayEmpty(data)) return;
        setStatuses(data);
      });
      setLoading(false);
    };

    if (open && Misc.isArrayEmpty(statuses)) fetchData();
  });

  const handleStatus = (e: any) => {
    e.preventDefault();
    setStatusId(e.target.value);
    setStatusChanged(true);
  };

  const invalid = () => {
    if (statusChanged)
      if (statusId == 20) return acceptedBy === "" || acceptedAt === null;
      else return false;

    return true;
  };

  const handleSubmit = async () => {
    if (invalid()) {
      AlertService.error(ToastMessages.ESTIMATE_STATUS_FAILED);
      return;
    }

    setLoading(true);

    const payload = {
      ...estimate,
      statusId: statusId,
      acceptedBy: acceptedBy,
      acceptedAt: acceptedAt,
    };
    await ApiService.put<Estimate>(
      `${DatabaseEntities.ESTIMATES}/${payload.code}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.ESTIMATE_STATUS_UPDATED);
        handleClose();
      })
      .catch((error) => {
        AlertService.error(ToastMessages.ESTIMATE_STATUS_FAILED);
        console.error("Error updating estimate:", error);
      })
      .finally(() => {
        setLoading(false);
        setStatusChanged(false);
        setAcceptedBy("");
        setAcceptedAt(null);
        estimate.statusId = statusId; // Update the estimate statusId
        estimate.acceptedBy = acceptedBy; // Update the acceptedBy field
        estimate.acceptedAt = acceptedAt ?? undefined; // Update the acceptedAt field
        // Optionally, you can reset the statusId to the initial value
        setStatusId(estimate.statusId);
        window.location.reload(); // Reload the page to reflect changes
      });
  };

  return (
    <div>
      <Btn
        onClick={handleOpen}
        className={classes.button}
        style={{ width: "100%" }}
      >
        Update status
      </Btn>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <div className={classes.headerSubDiv2}>
                <Typography className={classes.title}>Estimate</Typography>
                <div
                  className="vl"
                  style={{ marginLeft: "1rem", marginRight: "1rem" }}
                ></div>
                <Typography className={classes.subtitle}>
                  {estimate.code}
                </Typography>
              </div>
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.form2}>
              <Typography
                style={{
                  marginBottom: "1rem",
                  color: "#747477",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                }}
              >
                Update the estimate status
              </Typography>
              <label className={classes.label}>Status</label>
              <select
                className={classes.input2}
                value={statusId}
                onChange={handleStatus}
              >
                <option value="">Select status</option>
                {statuses.map((status: Status) => {
                  return (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  );
                })}
              </select>
              {statusChanged && statusId == 20 && (
                <>
                  <label className={classes.label}>By</label>
                  <input
                    className={classes.input2}
                    type="text"
                    value={acceptedBy}
                    onChange={(e) => setAcceptedBy(e.target.value)}
                  />
                  <label className={classes.label}>Date</label>
                  <DatePicker
                    className={classes.input2}
                    placeholderText="Select date"
                    selected={acceptedAt}
                    onChange={(date) => setAcceptedAt(date)}
                  />
                </>
              )}
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.button}
                  onClick={handleSubmit}
                  disabled={invalid()}
                >
                  Update
                </Btn>
              </div>
            </div>
          </Box>
        )}
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default UpdateStatusModal;
